import { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from '../redux/api';

/*
 * Loads a page of orders into local state.
 *
 * Replaces `dispatch(getOrderList(params))` plus a read of the global
 * `reusableSlice.OrderList`, which had the same defect the detail screen did:
 * the thunk swallowed every error into `setOrderList([])`, so a dropped
 * connection, an expired token and a genuinely empty tab all rendered the same
 * "No orders." — and the screen picked the reading that says the store is empty.
 *
 * Two states, not one. `isLoading` is the first load of a given query, when
 * there is nothing to show yet. `isRefetching` is every load after that, when
 * the previous page is still on screen — paging, searching, or a manual
 * refresh. Keeping the old rows visible during a refetch is what stops the
 * table collapsing to full height and back on every keystroke.
 *
 * A monotonic request id guards ordering: type quickly and the earlier, slower
 * response cannot overwrite the later one.
 */

const GENERIC_MESSAGE = 'Could not load orders.';

const readError = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.response?.message ||
  GENERIC_MESSAGE;

export const useOrderList = (params) => {
  const [state, setState] = useState({
    orders: [],
    pagination: null,
    error: null,
    hasLoaded: false,
    inFlight: true,
  });

  const requestRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestRef.current;
    setState((prev) => ({ ...prev, inFlight: true, error: null }));

    try {
      const response = await axiosInstance.get('admin/orders', { params });
      if (requestId !== requestRef.current) return;

      const payload = response?.data?.response ?? {};
      setState({
        orders: Array.isArray(payload.orders) ? payload.orders : [],
        pagination: payload.pagination ?? null,
        error: null,
        hasLoaded: true,
        inFlight: false,
      });
    } catch (error) {
      if (requestId !== requestRef.current) return;
      // Rows are cleared on failure: leaving stale rows under an error banner
      // invites acting on data that may no longer be true.
      setState({
        orders: [],
        pagination: null,
        error: readError(error),
        hasLoaded: true,
        inFlight: false,
      });
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(
    () => () => {
      requestRef.current += 1;
    },
    [],
  );

  return {
    orders: state.orders,
    pagination: state.pagination,
    error: state.error,
    isError: Boolean(state.error),
    // Nothing on screen yet — a skeleton belongs here.
    isLoading: state.inFlight && !state.hasLoaded,
    // Previous page still visible — a busy hint belongs here, not a skeleton.
    isRefetching: state.inFlight && state.hasLoaded,
    isEmpty: state.hasLoaded && !state.error && state.orders.length === 0,
    reload: load,
  };
};

export default useOrderList;
